import React, { useContext } from 'react';
import { RelationEntity } from '@interpals/api-web';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import reactStringReplace from 'react-string-replace';

import { WindowSizeContext } from 'contexts';
import ROUTES from 'routes';
import { makeProfileURL } from 'routes';
import { useAppDispatch } from 'hooks';
import { updateRelation } from 'redux/profile/actions';
import UserAvatar from 'components/UserAvatar';
import Button from 'components/ui/Button';
import Typography from 'components/ui/Typography';

import css from './FriendRequestsNotifications.module.scss';

interface Props {
    friendRequests: RelationEntity[];
}

const FriendRequestsNotifications: React.FC<Props> = ({ friendRequests }) => {
    const { t } = useTranslation();

    const dispatch = useAppDispatch();

    const navigate = useNavigate();

    const { isMobile } = useContext(WindowSizeContext);

    const renderButtons = (relation: RelationEntity): JSX.Element => {
        return (
            <div className={css.buttons}>
                <Button
                    withPadding
                    color="transparent"
                    onClick={() => {
                        dispatch(
                            updateRelation({
                                id: relation.target_id,
                                action: 'ignore_friend',
                            }),
                        );
                    }}
                >
                    {t('Common.Actions.Ignore')}
                </Button>
                <Button
                    withPadding
                    onClick={() => {
                        dispatch(
                            updateRelation({
                                id: relation.target_id,
                                action: 'accept_friend',
                            }),
                        );
                    }}
                >
                    {t('Common.Actions.Confirm')}
                </Button>
            </div>
        );
    };

    return (
        <>
            {friendRequests.slice(0, 2).map(
                (relation) =>
                    relation.user && (
                        <div className={css.row} key={relation.target_id}>
                            <div className={css.info}>
                                <Link to={makeProfileURL(relation.user.username)}>
                                    <div className={css.container}>
                                        <UserAvatar user={relation.user} size="M" />
                                    </div>
                                </Link>
                                <div className={css.mobileActions}>
                                    <Typography>
                                        {reactStringReplace(
                                            t('AlertsMenu.IncomingFriendRequest', { user: '{{user}}' }),
                                            '{{user}}',
                                            () =>
                                                relation.user && (
                                                    <a
                                                        href={makeProfileURL(relation.user.username)}
                                                        key={`link-${relation.user.id}`}
                                                    >
                                                        <Typography style="BodyMedSemibold" tag="span" color="gray">
                                                            {relation.user.name}
                                                        </Typography>
                                                    </a>
                                                ),
                                        )}
                                    </Typography>
                                    {isMobile && renderButtons(relation)}
                                </div>
                            </div>
                            {!isMobile && renderButtons(relation)}
                        </div>
                    ),
            )}
            {friendRequests.length > 2 && (
                <div className={css.showMore}>
                    <Button withPadding color="pale" onClick={() => navigate(ROUTES.FRIENDS_REQUEST)}>
                        {t('AlertsMenu.SeeMore', { number: String(friendRequests.length - 2) })}
                    </Button>
                </div>
            )}
        </>
    );
};

export default React.memo(FriendRequestsNotifications);
